import React, { useEffect, useState, useMemo } from "react";
import "../../Styles/invoiceTable.css";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import { BsSearch } from "react-icons/bs";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Customer = {
  _id: string;
  businessName: string;
  phone: string;
  email: string;
};

type Requisition = {
  supplyLocation: any;
  quantityLoaded: any;
  approvalNumber: string;
  customer: Customer | null;
  itemName: string;
  quantity: number;
  unitPrice: number;
};

type dailyAllocation = {
  _id: string;
  license: License | null;
};

type License = {
  _id: string;
  licenseNumber: string;
  licenseOwner: string;
  destination: string;
};

type TruckInOut = {
  truckNumber: any;
  _id: string;
  supplyLocation: string;
  destination: string;
  loadingNumber: string;
  requisitions: Requisition[];
  createdAt: string;
  updatedAt: string;
  driver: string;
  dailyAllocation: dailyAllocation | null;
  invoiced: boolean;
};

const TruckInOutReport = () => {
  const [truckInOuts, setTruckInOuts] = useState<TruckInOut[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");
    const dowBtnElement = document.querySelector(".dowBtn");

    if (slipElement) {
      if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "none";

      const pdfOptions = {
        margin: 2,
        filename: "TruckInOut-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };

      html2pdf()
        .from(slipElement)
        .set(pdfOptions)
        .save()
        .finally(() => {
          if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "block";
        });
    }
  };

  const calculateTotalQuantityLoaded = (data: TruckInOut[]) => {
    return data.reduce((total, truckInOut) => {
      return total + truckInOut.requisitions.reduce((subtotal, req) => {
        return subtotal + (req.quantityLoaded || 0);
      }, 0);
    }, 0);
  };

  const calculateCustomerQuantityLoaded = (data: TruckInOut[]) => {
    return data.reduce((acc, truckInOut) => {
      truckInOut.requisitions.forEach((requisition) => {
        const customerName = requisition.customer?.businessName || "Unknown";
        acc[customerName] = (acc[customerName] || 0) + (requisition.quantityLoaded || 0);
      });
      return acc;
    }, {} as Record<string, number>);
  };

  const token = window.localStorage.getItem("token");

  const queryTruckInOuts = () => {
    setIsLoading(true);
    setError(null);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    api
      .get(`/loading-slip`, { headers })
      .then((res) => {
        setTruckInOuts(res.data.data);
        console.log("Truck In/Out Data:", res.data.data);
      })
      .catch((error) => {
        if (
          error.response?.data?.message === "jwt expired" ||
          error.response?.data?.message === "jwt malformed"
        ) {
           
          setReLogin(true);
        } else {
          setError("Failed to fetch truck in/out data. Please try again later.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    queryTruckInOuts();
  }, []);

  const filteredTruckInOuts = useMemo(() => {
    return truckInOuts.filter((invoice) => {
      if (!invoice.invoiced) return false;

      const truckDate = new Date(invoice.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (startDate && truckDate < startDate) return false;
      if (endDate && truckDate > endDate) return false;

      if (searchField === "combined") {
        if (!businessQuery && !productQuery && !truckQuery) return true;
      } else if (!searchQuery) {
        return true;
      }

      const requisitions = invoice.requisitions || [];

      const businessNameMatch = requisitions.some((req: any) =>
        req?.customer?.businessName?.toLowerCase().includes(
          searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase()
        )
      );
      const productNameMatch = requisitions.some((req: any) =>
        req?.itemName?.toLowerCase().includes(
          searchField === "combined" ? productQuery.toLowerCase() : searchQuery.toLowerCase()
        )
      );
      const truckNumberMatch = invoice.truckNumber?.toLowerCase().includes(
        searchField === "combined" ? truckQuery.toLowerCase() : searchQuery.toLowerCase()
      );

      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "productName":
          return productNameMatch;
        case "truckNumber":
          return truckNumberMatch;
        case "combined":
          return businessNameMatch && productNameMatch && truckNumberMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [truckInOuts, fromDate, toDate, searchQuery, productQuery, businessQuery, truckQuery, searchField]);

  const paginatedTruckInOuts = filteredTruckInOuts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate totals on the full filtered dataset
  const totalQuantityLoaded = calculateTotalQuantityLoaded(filteredTruckInOuts);
  const customerQuantities = calculateCustomerQuantityLoaded(filteredTruckInOuts);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    if (!filteredTruckInOuts || filteredTruckInOuts.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredTruckInOuts.flatMap((truckInOut, index) => {
      return truckInOut.requisitions.map((requisition, reqIndex) => ({
        "S/N": index + 1,
        "Customer Name": requisition.customer?.businessName || "N/A",
        "Destination": truckInOut.destination || "N/A",
        "Approval Number": requisition.approvalNumber || "N/A",
        "Item Name": requisition.itemName || "N/A",
        "Quantity": requisition.quantityLoaded || 0,
        "Truck Number": truckInOut.truckNumber || "N/A",
        "Unit Price": requisition.unitPrice || 0,
        "Driver": truckInOut.driver || "N/A",
        "Created At": new Date(truckInOut.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TruckInOutReports");
    XLSX.writeFile(wb, "TruckInOut-Report.xlsx");
  };

  return (
    <div className="board">
      <div className="method" style={{ marginTop: 0 }}>
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <label htmlFor="businessQuery" className="sr-only">Customer Name</label>
            <input
              id="businessQuery"
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <label htmlFor="productQuery" className="sr-only">Product Name</label>
            <input
              id="productQuery"
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <label htmlFor="truckQuery" className="sr-only">Truck Number</label>
            <input
              id="truckQuery"
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <label htmlFor="searchQuery" className="sr-only">Search</label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <label htmlFor="fromDate" className="sr-only">From Date</label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <label htmlFor="toDate" className="sr-only">To Date</label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {reLogin ? <ReLoginModal /> : null}
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : error ? (
          <div className="m-auto text-center px-4 py-4">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        ) : paginatedTruckInOuts.length > 0 ? (
          <>
            <div className="table-wrapper">
              <table className="inTable">
                <thead>
                  <tr className="heTable">
                    <th>S/N</th>
                    <th>Customer Name</th>
                    <th>Destination</th>
                    <th>Approval Number</th>
                    <th>Product Name</th>
                    <th>Quantity Loaded</th>
                    <th>Truck No</th>
                    <th>Unit Price</th>
                    <th>Driver</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTruckInOuts.map((truckInOut, index) => {
                    const formattedDate = new Date(truckInOut.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return truckInOut.requisitions.map((req, reqIndex) => (
                      <tr key={`${truckInOut._id}-${reqIndex}`} className="taData">
                        {reqIndex === 0 && (
                          <>
                            <td rowSpan={truckInOut.requisitions.length}>{page * rowsPerPage + index + 1}</td>
                            <td rowSpan={truckInOut.requisitions.length}>
                              {req.customer?.businessName || "N/A"}
                            </td>
                            <td rowSpan={truckInOut.requisitions.length} className="max-w-xs">
                              {truckInOut.destination || "N/A"}
                            </td>
                          </>
                        )}
                        <td>{req.approvalNumber || "N/A"}</td>
                        <td>{req.itemName || "N/A"}</td>
                        <td>{req.quantityLoaded || 0}</td>
                        {reqIndex === 0 && (
                          <>
                            <td rowSpan={truckInOut.requisitions.length}>
                              {truckInOut.truckNumber || "N/A"}
                            </td>
                            <td rowSpan={truckInOut.requisitions.length}>{req.unitPrice || 0}</td>
                            <td rowSpan={truckInOut.requisitions.length}>{truckInOut.driver || "N/A"}</td>
                            <td rowSpan={truckInOut.requisitions.length}>{formattedDate}</td>
                          </>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
            <div className="aggregate">
              {Object.entries(customerQuantities).map(([customer, quantity]) => (
                <div key={customer}>
                  <strong>{customer}: </strong>
                  {quantity}
                </div>
              ))}
              <div>
                <strong>Total Quantity Loaded: </strong>
                {totalQuantityLoaded}
              </div>
            </div>
          </>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredTruckInOuts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default TruckInOutReport;